import { useCallback, useState } from 'react';
import ChatService from '../services/ChatService';
import TriggersService from '../services/TriggersService';
import { errorText } from '../utils/format';
import useAsync from './useAsync';

/** Triggers: list, create, edit, switch on or off, check now, delete. */
export function useTriggersViewModel() {
  const load = useCallback(async () => {
    const [triggers, chats] = await Promise.all([TriggersService.list(), ChatService.conversations()]);
    return { triggers, chats };
  }, []);
  const { data, loading, error, reload } = useAsync(load);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [feedback, setFeedback] = useState(null);

  const run = async (fn, successText) => {
    setSaving(true);
    setSaveError('');
    try {
      const result = await fn();
      await reload({ silent: true });
      const text = typeof successText === 'function' ? successText(result) : successText;
      if (text) setFeedback({ severity: 'success', text });
      return true;
    } catch (err) {
      setSaveError(errorText(err));
      if (!successText) return false;
      setFeedback({ severity: 'error', text: errorText(err) });
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    triggers: data?.triggers || [],
    chats: data?.chats || [],
    loading,
    error,
    reload,
    saving,
    saveError,
    clearSaveError: () => setSaveError(''),
    feedback,
    clearFeedback: () => setFeedback(null),
    create: (t) => run(() => TriggersService.create(t)),
    update: (id, fields) => run(() => TriggersService.update(id, fields)),
    toggle: (t) => run(() => TriggersService.update(t.id, { enabled: !t.enabled }), t.enabled ? 'Trigger switched off.' : 'Trigger switched on.'),
    remove: (id) => run(() => TriggersService.remove(id), 'Trigger deleted.'),
    checkNow: (id) => run(() => TriggersService.checkNow(id), (t) => (t.last_status === 'fired'
      ? 'Something new was found. Open the chat to follow along.'
      : 'Checked. Nothing new to report right now.')),
  };
}
